import React from "react";

import { animated, SpringValue } from "@react-spring/konva";
import Konva from "konva";
import { Group, Line, Rect } from "react-konva";

import { Segment } from "./Segment";
import { useCoverageAdditions } from "./useCoverageReveal";
import { useSegmentGrowth } from "./useSegmentGrowth";
import { useSegmentRetractions, type SegmentRetraction } from "./useSegmentRetractions";
import { TRACK_BORDER_COLOR } from "../../constants";
import { TAnimatedStyle, TRenderableRow } from "../../types";
import { collectRenderableSegments, trackToBars, type TRenderableSegment } from "../../utils/tree";
import { projectTimelineSegments } from "../../tracking/player/timeline-status";
import { isTimelineFocusTarget, type AnnotationFocus } from "../../utils/focus";
import type { TimelineStore } from "../../store/timeline";
import { useTimelineStore } from "../../store/timeline-context";

/**
 * Type for animated Konva Group component props.
 * Using explicit type to avoid deep type recursion from react-spring.
 */
type AnimatedGroupProps = {
  /** Clipping function for canvas rendering bounds */
  clipFunc?: (ctx: Konva.Context) => void;
  /** Vertical position (can be animated) */
  y?: number | SpringValue<number>;
  /** Height (can be animated) */
  height?: number | SpringValue<number>;
  /** Opacity (can be animated) */
  opacity?: number | SpringValue<number>;
  /** React key for list rendering */
  key?: string;
  /** Child components to render */
  children?: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AnimatedGroup = animated.Group as any as React.ComponentType<AnimatedGroupProps>;

/**
 * Props for the AnimatedRow component
 */
export type AnimatedRowProps = {
  focus: AnnotationFocus | null;
  /** React-spring animation values for position, size, and opacity */
  style: TAnimatedStyle;
  /** Row data containing track or collection header information */
  row: TRenderableRow;
  /** Total timeline width in pixels */
  timelineWidth: number;
  /** Current zoom level in pixels per second */
  pixelsPerSecond: number;
  /** Left edge of visible viewport (for culling) */
  visibleStartPx: number;
  /** Right edge of visible viewport (for culling) */
  visibleEndPx: number;
  /** Total timeline duration in seconds */
  durationSeconds: number;
  /** Callback when a segment is clicked */
  onSegmentClick?: (time: number) => void;
};

/** Round the media-clamped silhouette itself, rather than cutting off an out-of-bounds cap. */
function segmentWithinDuration(segment: TRenderableSegment, duration: number): TRenderableSegment | null {
  const originalEnd = segment.end ?? segment.start;
  if (originalEnd === segment.start) return segment.start >= 0 && segment.start <= duration ? segment : null;
  const start = Math.max(0, segment.start);
  const end = Math.min(duration, originalEnd);
  if (!(start < end)) return null;
  if (start === segment.start && end === originalEnd) return segment;
  return { ...segment, start, end, ...(segment.visibleRange ? { visibleRange: { start, end } } : {}) };
}

function retractionWithinDuration(motion: SegmentRetraction | undefined, duration: number) {
  if (!motion || (motion.fromStart >= 0 && motion.fromEnd <= duration && motion.start >= 0 && motion.end <= duration))
    return motion;
  return {
    ...motion,
    start: Math.max(0, motion.start),
    end: Math.min(duration, motion.end),
    fromStart: Math.max(0, motion.fromStart),
    fromEnd: Math.min(duration, motion.fromEnd),
    fromLeftRadius: motion.fromStart < 0 ? 1 : motion.fromLeftRadius,
    fromRightRadius: motion.fromEnd > duration ? 1 : motion.fromRightRadius,
  };
}

/**
 * Animated timeline row component with viewport culling and custom memoization.
 *
 * Renders either a track with segments or a collection header with summary visualization.
 * Uses react-spring for smooth enter/exit/update animations and viewport culling to only
 * render segments visible in the current viewport.
 *
 * Performance optimizations:
 * - Custom memo comparison (1px threshold for position changes)
 * - Viewport culling for segments
 * - Clipping for overflow prevention
 */
export const AnimatedRow: React.FC<AnimatedRowProps> = React.memo(
  ({
    focus,
    style,
    row,
    timelineWidth,
    pixelsPerSecond,
    visibleStartPx,
    visibleEndPx,
    durationSeconds,
    onSegmentClick,
  }) => {
    const rowHeight = row.height;
    const canFocus = React.useMemo(() => isTimelineFocusTarget(row.data), [row.data]);
    const trackingTimeline = useTimelineStore((state) => state.trackingTimeline);
    const setHoveredRow = useTimelineStore((state) => state.setHoveredTimelineRow);
    const clearHoveredRow = useTimelineStore((state) => state.clearHoveredTimelineRow);
    React.useEffect(() => () => clearHoveredRow(row.data.id, "canvas"), [row.data.id, clearHoveredRow]);

    const rowSegments = React.useMemo(() => {
      let segments: TRenderableSegment[] = [];
      if (row.kind === "track") {
        segments = trackToBars(row.data);
      } else if (row.kind === "bare-segment") {
        segments = [row.data];
      } else if (row.kind === "collection-header" && row.isCollapsed && row.depth > 0) {
        // Render nested tracks as cluster bars (not per-waypoint diamonds) when their parent collection is collapsed.
        segments = collectRenderableSegments(row.data.children);
      }

      return segments;
    }, [row]);

    // Moving the hover between bars re-renders only the rows that hold them.
    // Every row still re-renders when a hover starts or ends, since that dims
    // or restores bars everywhere.
    const rowSegmentIds = React.useMemo(() => new Set(rowSegments.map((segment) => segment.id)), [rowSegments]);
    const hoverInRow = (state: TimelineStore) =>
      state.hoveredTimelineSegmentId !== null && rowSegmentIds.has(state.hoveredTimelineSegmentId);
    const hoveredSegmentId = useTimelineStore((state) => (hoverInRow(state) ? state.hoveredTimelineSegmentId : null));
    const hoveredRange = useTimelineStore((state) => (hoverInRow(state) ? state.hoveredTimelineSegmentRange : null));
    const anySegmentHovered = useTimelineStore((state) => state.hoveredTimelineSegmentId !== null);
    const clipHovered = useTimelineStore(
      (state) => state.hoveredTimelineSegmentId !== null && state.hoveredTimelineRow === null,
    );

    const coverageAdditions = useCoverageAdditions(trackingTimeline, rowSegments);
    const segmentGrowth = useSegmentGrowth(rowSegments, trackingTimeline);
    const segmentRetractions = useSegmentRetractions(trackingTimeline, rowSegments);
    const visibleSegments = React.useMemo(() => {
      return projectTimelineSegments(rowSegments, trackingTimeline).flatMap((source) => {
        // Raw GT/fallback bars may extend beyond the video's last frame. Clamp
        // their geometry before Segment rounds it; viewport clipping still
        // handles zoom/pan without inventing endpoints at the screen edge.
        const segment = segmentWithinDuration(source, durationSeconds);
        if (!segment) return [];
        const retraction = segmentRetractions.get(`${segment.id}:${segment.start}`);
        const startPx = Math.min(segment.start, retraction?.fromStart ?? segment.start) * pixelsPerSecond;

        if (!segment.end || segment.end === segment.start) {
          return startPx >= visibleStartPx && startPx <= visibleEndPx ? [segment] : [];
        }

        const endPx = Math.max(segment.end, retraction?.fromEnd ?? segment.end) * pixelsPerSecond;
        return endPx > visibleStartPx && startPx < visibleEndPx ? [segment] : [];
      });
    }, [
      rowSegments,
      pixelsPerSecond,
      visibleStartPx,
      visibleEndPx,
      trackingTimeline,
      segmentRetractions,
      durationSeconds,
    ]);

    const clipFunc = React.useCallback(
      (ctx: Konva.Context) => {
        ctx.rect(0, 0, timelineWidth, rowHeight);
      },
      [timelineWidth, rowHeight],
    );

    if (!(timelineWidth > 0) || !(rowHeight > 0)) return null;

    return React.createElement(
      AnimatedGroup,
      {
        clipFunc,
        height: style.height,
        key: row.id,
        opacity: style.opacity,
        y: style.y,
        onMouseEnter: () => {
          if (canFocus) setHoveredRow(row.data.id, "canvas");
        },
        onMouseLeave: () => clearHoveredRow(row.data.id, "canvas"),
      },
      // The full row participates in hover even where there is no annotation.
      // This remains a background hit, so dragging still scrubs the timeline.
      React.createElement(Rect, {
        name: "timeline-row-background",
        width: timelineWidth,
        height: rowHeight,
        fill: "transparent",
        onMouseEnter: () => {
          if (canFocus) setHoveredRow(row.data.id, "canvas");
        },
      }),
      React.createElement(Line, {
        listening: false,
        points: [0, 0, timelineWidth, 0],
        stroke: TRACK_BORDER_COLOR,
        strokeWidth: 2,
      }),
      visibleSegments.length > 0 &&
        React.createElement(
          Group,
          {},
          visibleSegments.map((segment) => {
            const isExactHover =
              hoveredSegmentId === segment.id &&
              (!hoveredRange ||
                (segment.visibleRange?.start === hoveredRange.start && segment.visibleRange?.end === hoveredRange.end));
            return React.createElement(Segment, {
              key: `${segment.id}:${segment.start}`,
              pixelsPerSecond: pixelsPerSecond,
              rowHeight: rowHeight,
              rowY: 0,
              segment: segment,
              coverageAdditions: coverageAdditions.get(segment.id),
              growth: segmentGrowth.get(segment.id),
              retraction: retractionWithinDuration(
                segmentRetractions.get(`${segment.id}:${segment.start}`),
                durationSeconds,
              ),
              statusRuns: trackingTimeline?.segments.get(segment.id)?.runs,
              collectionId: row.kind === "collection-header" ? row.data.id : undefined,
              isHighlighted: isExactHover
                ? true
                : !segment.groupId
                  ? segment.type === "clip" && clipHovered
                    ? false
                    : null
                  : anySegmentHovered
                    ? false
                    : focus
                      ? focus.ids.has("groupId" in segment ? String(segment.groupId) : segment.id)
                      : null,
              onSegmentClick: onSegmentClick,
            });
          }),
        ),
    );
  },
  (prevProps, nextProps) => {
    // Extract numeric values from SpringValue
    const prevY = typeof prevProps.style.y === "number" ? prevProps.style.y : prevProps.style.y.get();
    const nextY = typeof nextProps.style.y === "number" ? nextProps.style.y : nextProps.style.y.get();
    const prevHeight =
      typeof prevProps.style.height === "number" ? prevProps.style.height : prevProps.style.height.get();
    const nextHeight =
      typeof nextProps.style.height === "number" ? nextProps.style.height : nextProps.style.height.get();

    // Check if style changed significantly (1px threshold for positions)
    const styleChanged =
      Math.abs(prevY - nextY) >= 1 ||
      Math.abs(prevHeight - nextHeight) >= 1 ||
      prevProps.style.opacity !== nextProps.style.opacity;

    // `row.id` is stable across collapse/expand; explicit `isCollapsed` /
    // `data` / `kind` checks are required for collapse to actually re-render.
    const propsChanged =
      prevProps.focus !== nextProps.focus ||
      prevProps.row.id !== nextProps.row.id ||
      prevProps.row.isCollapsed !== nextProps.row.isCollapsed ||
      prevProps.row.kind !== nextProps.row.kind ||
      prevProps.row.data !== nextProps.row.data ||
      prevProps.row.depth !== nextProps.row.depth ||
      prevProps.pixelsPerSecond !== nextProps.pixelsPerSecond ||
      prevProps.visibleStartPx !== nextProps.visibleStartPx ||
      prevProps.visibleEndPx !== nextProps.visibleEndPx ||
      prevProps.timelineWidth !== nextProps.timelineWidth ||
      prevProps.durationSeconds !== nextProps.durationSeconds ||
      prevProps.onSegmentClick !== nextProps.onSegmentClick;

    // Return true to skip re-render, false to re-render
    return !styleChanged && !propsChanged;
  },
);

AnimatedRow.displayName = "AnimatedRow";
